import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { adminGetCase, adminCreateCase, adminUpdateCase, getMeta } from "@/lib/apiClient";
import { toast } from "sonner";

const ACCENTS = ["blue", "orange", "lime", "pink", "yellow"];
const STATUSES = ["Draft", "Published", "Rejected"];

const BLANK = {
  company: "", headline: "", summary: "", country: "", region: "", industry: "",
  business_type: "Traditional Business", company_size: "", transformation_types: [],
  before_ai: "", what_changed: "", result: "", key_metric: "", metric_context: "",
  why_this_matters_headline: "", why_this_matters_body: "", technology_provider: "",
  technology_product: "", technology_use: [], source_organisation: "", source_type: "",
  source_title: "", source_url: "", publication_date: "", image: "", accent: "blue",
  tags: [], featured: false, hero: false, evergreen: false, status: "Draft",
};

function Field({ label, children }) {
  return (
    <div>
      <label className="eyebrow text-ink/60 block mb-1">{label}</label>
      {children}
    </div>
  );
}
const inputCls = "w-full h-11 px-3 border-2 border-ink outline-none focus:shadow-[3px_3px_0_0_#0A0A0C] transition-shadow bg-white";
const areaCls = "w-full p-3 border-2 border-ink outline-none focus:shadow-[3px_3px_0_0_#0A0A0C] transition-shadow bg-white min-h-[90px]";

export default function AdminEditor() {
  const { slug } = useParams();
  const isNew = slug === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(BLANK);
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getMeta().then(setMeta);
    if (!isNew) adminGetCase(slug).then(setForm).catch(() => { toast.error("Case not found"); navigate("/admin/cases"); });
  }, [slug]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTT = (tt) => setForm((f) => ({
    ...f, transformation_types: f.transformation_types.includes(tt)
      ? f.transformation_types.filter((x) => x !== tt) : [...f.transformation_types, tt],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      technology_use: Array.isArray(form.technology_use) ? form.technology_use : String(form.technology_use).split(",").map((s) => s.trim()).filter(Boolean),
      tags: Array.isArray(form.tags) ? form.tags : String(form.tags).split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (isNew) { await adminCreateCase(payload); toast.success("Case created"); }
      else { await adminUpdateCase(slug, payload); toast.success("Case saved"); }
      navigate("/admin/cases");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const asCSV = (v) => (Array.isArray(v) ? v.join(", ") : v || "");

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/admin/cases" className="group inline-flex items-center gap-2 font-semibold text-sm text-ink/70">
        <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" /> Back to cases
      </Link>
      <h1 className="font-display font-extrabold text-4xl display-tight uppercase mt-4">{isNew ? "Add Case" : "Edit Case"}</h1>

      <form onSubmit={submit} className="mt-8 space-y-8" data-testid="admin-add-case-form">
        <section className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Company"><input required className={inputCls} value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="case-company-input" /></Field>
            <Field label="Industry"><input className={inputCls} value={form.industry} onChange={(e) => set("industry", e.target.value)} data-testid="case-industry-input" /></Field>
          </div>
          <Field label="Headline"><input required className={inputCls} value={form.headline} onChange={(e) => set("headline", e.target.value)} data-testid="case-headline-input" /></Field>
          <Field label="Summary"><textarea className={areaCls} value={form.summary} onChange={(e) => set("summary", e.target.value)} /></Field>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Country"><input className={inputCls} value={form.country} onChange={(e) => set("country", e.target.value)} /></Field>
            <Field label="Region"><input className={inputCls} value={form.region} onChange={(e) => set("region", e.target.value)} /></Field>
            <Field label="Company Size"><input className={inputCls} value={form.company_size} onChange={(e) => set("company_size", e.target.value)} /></Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Business Type">
              <select className={inputCls} value={form.business_type} onChange={(e) => set("business_type", e.target.value)} data-testid="case-business-type-select">
                {(meta?.business_types || []).map((b) => <option key={b}>{b}</option>)}
              </select>
            </Field>
            <Field label="Image URL"><input className={inputCls} value={form.image} onChange={(e) => set("image", e.target.value)} /></Field>
          </div>
          {form.image && <img src={form.image} alt="preview" className="h-40 w-full object-cover border-2 border-ink" />}
        </section>

        <section>
          <label className="eyebrow text-ink/60 block mb-2">Transformation Types</label>
          <div className="flex flex-wrap gap-2">
            {(meta?.transformation_types || []).map((tt) => (
              <button type="button" key={tt} onClick={() => toggleTT(tt)}
                className={`px-3 h-9 border-2 border-ink text-sm font-semibold ${form.transformation_types.includes(tt) ? "bg-ink text-white" : "bg-white hover:bg-bryellow"}`}>
                {tt}
              </button>
            ))}
          </div>
        </section>

        <section className="space-y-4">
          <Field label="Before AI"><textarea className={areaCls} value={form.before_ai} onChange={(e) => set("before_ai", e.target.value)} /></Field>
          <Field label="What Changed"><textarea className={areaCls} value={form.what_changed} onChange={(e) => set("what_changed", e.target.value)} /></Field>
          <Field label="The Result"><textarea className={areaCls} value={form.result} onChange={(e) => set("result", e.target.value)} /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Key Metric (e.g. UP TO 30%)"><input className={inputCls} value={form.key_metric} onChange={(e) => set("key_metric", e.target.value)} /></Field>
            <Field label="Metric Context"><input className={inputCls} value={form.metric_context} onChange={(e) => set("metric_context", e.target.value)} /></Field>
          </div>
        </section>

        <section className="space-y-4">
          <Field label="Why This Matters — Headline"><input className={inputCls} value={form.why_this_matters_headline} onChange={(e) => set("why_this_matters_headline", e.target.value)} /></Field>
          <Field label="Why This Matters — Body (use blank lines for paragraphs)"><textarea className={areaCls} value={form.why_this_matters_body} onChange={(e) => set("why_this_matters_body", e.target.value)} /></Field>
        </section>

        <section className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Technology Provider"><input className={inputCls} value={form.technology_provider} onChange={(e) => set("technology_provider", e.target.value)} /></Field>
            <Field label="Technology Product"><input className={inputCls} value={form.technology_product} onChange={(e) => set("technology_product", e.target.value)} /></Field>
          </div>
          <Field label="Technology Use (comma separated)"><input className={inputCls} value={asCSV(form.technology_use)} onChange={(e) => set("technology_use", e.target.value)} /></Field>
        </section>

        <section className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Source Organisation"><input className={inputCls} value={form.source_organisation} onChange={(e) => set("source_organisation", e.target.value)} /></Field>
            <Field label="Source Type"><input className={inputCls} value={form.source_type} onChange={(e) => set("source_type", e.target.value)} /></Field>
          </div>
          <Field label="Source Title"><input className={inputCls} value={form.source_title} onChange={(e) => set("source_title", e.target.value)} /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Source URL"><input className={inputCls} value={form.source_url} onChange={(e) => set("source_url", e.target.value)} data-testid="case-source-url-input" /></Field>
            <Field label="Publication Date"><input className={inputCls} value={form.publication_date} onChange={(e) => set("publication_date", e.target.value)} /></Field>
          </div>
          <Field label="Tags (comma separated)"><input className={inputCls} value={asCSV(form.tags)} onChange={(e) => set("tags", e.target.value)} /></Field>
        </section>

        <section className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 items-end">
          <Field label="Accent Colour">
            <select className={inputCls} value={form.accent} onChange={(e) => set("accent", e.target.value)}>
              {ACCENTS.map((a) => <option key={a}>{a}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="case-status-select">
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <label className="flex items-center gap-2 h-11 font-semibold">
            <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="h-5 w-5 accent-black" data-testid="case-featured-checkbox" />
            Featured
          </label>
          <label className="flex items-center gap-2 h-11 font-semibold" title="Pin to the landing-page top-right slot (only one case can be the hero)">
            <input type="checkbox" checked={form.hero} onChange={(e) => set("hero", e.target.checked)} className="h-5 w-5 accent-black" data-testid="case-hero-checkbox" />
            Landing Hero
          </label>
          <label className="flex items-center gap-2 h-11 font-semibold" title="Evergreen cases are never auto-retired/archived">
            <input type="checkbox" checked={form.evergreen} onChange={(e) => set("evergreen", e.target.checked)} className="h-5 w-5 accent-black" data-testid="case-evergreen-checkbox" />
            Evergreen
          </label>
        </section>

        <button disabled={busy} data-testid="case-save-button" className="group btn-tactile inline-flex items-center gap-2 bg-ink text-white font-bold px-7 h-13 py-3 hover:bg-cobalt disabled:opacity-60">
          <Save size={18} /> {busy ? "Saving…" : "Save Case"}
        </button>
      </form>
    </div>
  );
}
